// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';
import Text from '../Typography/Text/Text';
import { StyledAvatar, AvatarIcon, AvatarInfo } from './styles';
import * as avatarImages from './components';

/**
 * Prop migration guide
 *
 * | OLD                | NEW                 |
 * |--------------------|---------------------|
 * | name               | primaryText         |
 * | secondaryInfo      | secondaryText       |
 * | isCompany          | variant = 'company' |
 * | noIcon             | variant = 'none'    |
 *
 * Ignored props:
 * - `isCell` is a spacing modifier for the old Table component so
 *    it's been hidden
 * - `isInsideButton` is a spacing modifier but has been scoped in to
 *  the styles by default
 */

export interface AvatarProps {
  /** First line of content in the avatar information */
  primaryText?: ReactNode;
  /** Second line of content in the avatar information */
  secondaryText?: ReactNode;

  /** Reference to the image URL that will be displayed */
  imageUrl?: string;

  /** Initials that are placed inside of the avatar icon */
  initials?: string;
  /** Modifier distinguishing between multiple Avatars */
  initialsVariant?: 'primary' | 'secondary' | 'tertiary';

  /** Graphic variants */
  variant?: 'carta' | 'company' | 'none';

  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore - use primaryText instead */
  name?: ReactNode;
  /** @ignore - use secondaryText instead */
  secondaryInfo?: ReactNode;
  /** @ignore - use Variant instead; Modifier for company logos */
  isCompany?: boolean;
  /** @ignore - use Variant instead; Modifier to remove icon */
  noIcon?: boolean;
  /** @ignore - Spacing modifier for (deprecated) Tables */
  isCell?: boolean;
  /** @ignore - Spacing modifier when used in Buttons (scoped in style) */
  isInsideButton?: boolean;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Shows thumbnails for people and company logos
 * @categories accessory
 * @aka user profile, profile image, user image
 */
function Avatar(props: AvatarProps) {
  const {
    imageUrl,
    initials,
    initialsVariant,
    isCell,
    isCompany,
    isInsideButton,
    name,
    noIcon,
    primaryText,
    secondaryInfo,
    secondaryText,
    variant,
    ...otherProps
  } = props;

  const company = variant === 'company' || isCompany;
  const hasPrimaryText = name || primaryText;
  const hasSecondaryText = secondaryInfo || secondaryText;
  const hasAvatar = !noIcon && variant !== 'none';

  const getAvatarContent = () => {
    if (imageUrl) return null;
    if (initials) return <span>{initials}</span>;
    if (variant === 'carta') return avatarImages.carta;
    if (company) return avatarImages.company;
    return avatarImages.person;
  };

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        avatarCell: isCell,
        avatarCompany: company,
        avatarInitials: !!initials,
        avatarInitialsVariant: initialsVariant,
        avatarSecondaryText: hasSecondaryText,
      })}
    >
      <StyledAvatar {...globalProps} data-testid={otherProps['data-testid']} {...otherProps}>
        {hasAvatar && (
          <AvatarIcon style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}>
            {getAvatarContent()}
          </AvatarIcon>
        )}

        {hasPrimaryText && (
          <AvatarInfo>
            <Text variant="body-2" trim>
              {name || primaryText}
            </Text>
            {hasSecondaryText && (
              <Text variant="body-3-feedback-neutral" trim>
                {hasSecondaryText}
              </Text>
            )}
          </AvatarInfo>
        )}
      </StyledAvatar>
    </ThemeProvider>
  );
}

Avatar.defaultProps = {
  isCell: false,
  isCompany: false,
  isInsideButton: false,
  noIcon: false,
};

Avatar.displayName = 'Avatar';

export default Avatar;
